//src/utils/appVersions.ts

// Aero-Command's version numbers and its revision log (Policies & Procedures
// Manual §8 — Software Revision Control; AC 120-78B).
//
//   App version     package.json "version", stamped into the build with the
//                   build time and Git commit (vite.config.ts).
//   Module versions src/utils/appModuleVersions.json — one per module; Part 141
//                   (School Module) is its own module.
//   Revision log    Firestore RevisionLog/{entryId} — append-only (firestore.rules):
//                   nothing is ever edited or deleted. A release is one entry;
//                   its sign-off, FAA notice sent and FAA acceptance are later
//                   entries that point to it (releaseId).
//
// §8.1: a Part 141 release that touches any of the four categories is
// regulatory-impacting — it needs the Chief Pilot's sign-off (§8.2) and
// written FAA notice before use for Part 141 records (§8.4); an emergency fix
// (§8.5) goes out first, with notice within 10 business days.

import {
  addDoc,
  collection,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  type Timestamp
} from 'firebase/firestore'
import { auth, db } from '@/firebase'
import moduleList from './appModuleVersions.json'

// Set by vite.config.ts at build time.
declare const __APP_VERSION__: string
declare const __BUILD_TIME__: string
declare const __GIT_COMMIT__: string

// (Missing only where vite.config.ts isn't used, e.g. a separate test config.)
const stamp = (value: () => string, fallback: string) => {
  try {
    return value()
  } catch {
    return fallback
  }
}

/** "2.0" from package.json's "2.0.0" (a third number shows only when it isn't 0). */
export const APP_VERSION = stamp(() => __APP_VERSION__, '0.0.0').replace(/\.0$/, '')
export const BUILD_TIME = new Date(stamp(() => __BUILD_TIME__, new Date().toISOString()))
/** Short Git commit of the build ('' when built outside Git). */
export const GIT_COMMIT = stamp(() => __GIT_COMMIT__, '')

/* ── Modules ────────────────────────────────────────────────────────────── */

export interface AppModule {
  id: string
  name: string
  version: string
  /** Its releases follow Manual §8 (categories, sign-off, FAA notice). */
  faaRegulated?: boolean
  paths?: string[]
}

export const APP_MODULES: AppModule[] = moduleList.modules

/** -1 / 0 / 1 — "2.0" vs "2.1", number by number. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0)
    if (d) return d < 0 ? -1 : 1
  }
  return 0
}

/**
 * The Versions page and the revision log are off until the app reaches
 * revisionLogStartsAt (2.1 — the FAA's acceptance); until then only the
 * version shows.
 */
export const REVISION_LOG_ACTIVE = compareVersions(APP_VERSION, moduleList.revisionLogStartsAt) >= 0

export const moduleById = (id: string) => APP_MODULES.find((m) => m.id === id)

/** "Check Rides v2.0" — for a module's page. */
export const moduleVersionLabel = (id: string) => {
  const m = moduleById(id)
  return m ? `${m.name} v${m.version}` : ''
}

/* ── The four §8.1 categories ───────────────────────────────────────────── */

export const REVISION_CATEGORIES = [
  {
    key: 'entry',
    title: 'Record entry',
    hint: 'How a record is created or what data it captures.'
  },
  {
    key: 'display',
    title: 'Record display',
    hint: 'What a user sees when viewing a record or manual.'
  },
  {
    key: 'access',
    title: 'Record/manual access',
    hint: 'Who may view, sign or modify a record or manual type.'
  },
  {
    key: 'quality',
    title: 'Data quality',
    hint: 'Calculations or validation behind a signed value (e.g. course-time totals).'
  }
] as const

export type RevisionCategory = (typeof REVISION_CATEGORIES)[number]['key']

export const categoryTitle = (key: string) =>
  REVISION_CATEGORIES.find((c) => c.key === key)?.title ?? key

/** §8.1: an FAA-regulated module's release touching any category. */
export const isRegulatory = (moduleId: string, categories: string[]) =>
  !!moduleById(moduleId)?.faaRegulated && categories.length > 0

/* ── The log ────────────────────────────────────────────────────────────── */

interface EntryBase {
  id: string
  createdAt: Date | null
  createdBy: string
  createdByName: string
}

export interface ReleaseEntry extends EntryBase {
  type: 'release'
  module: string
  version: string
  /** yyyy-mm-dd deployed. */
  deployedDate: string
  description: string
  categories: RevisionCategory[]
  regulatory: boolean
  /** §8.5 emergency fix — deployed first, FAA notice within 10 business days. */
  emergency: boolean
  /** The app build it was logged from. */
  appVersion: string
  commit: string
}

/** A follow-up to a release: sign-off (§8.2), FAA notice sent / accepted (§8.4). */
export interface FollowUpEntry extends EntryBase {
  type: 'signoff' | 'faaSent' | 'faaAccepted'
  releaseId: string
  /** yyyy-mm-dd — the notice's or acceptance's date ('' for a sign-off). */
  date: string
  note: string
  /** Sign-off: the school the signer is Chief Pilot of. */
  storeId: string
}

export type RevisionEntry = ReleaseEntry | FollowUpEntry

/** A release with its follow-ups, as shown. */
export interface Release extends ReleaseEntry {
  signoff: FollowUpEntry | null
  faaSent: FollowUpEntry | null
  faaAccepted: FollowUpEntry | null
  /** Emergency fix: last day for the FAA notice (yyyy-mm-dd). */
  noticeDue: string
}

const LOG = 'RevisionLog'

function fromDoc(id: string, x: Record<string, unknown>): RevisionEntry {
  const base = {
    id,
    createdAt: (x.createdAt as Timestamp | null)?.toDate?.() ?? null,
    createdBy: String(x.createdBy ?? ''),
    createdByName: String(x.createdByName ?? '')
  }
  if (x.type === 'release')
    return {
      ...base,
      type: 'release',
      module: String(x.module ?? ''),
      version: String(x.version ?? ''),
      deployedDate: String(x.deployedDate ?? ''),
      description: String(x.description ?? ''),
      categories: (Array.isArray(x.categories) ? x.categories : []) as RevisionCategory[],
      regulatory: x.regulatory === true,
      emergency: x.emergency === true,
      appVersion: String(x.appVersion ?? ''),
      commit: String(x.commit ?? '')
    }
  return {
    ...base,
    type: x.type as FollowUpEntry['type'],
    releaseId: String(x.releaseId ?? ''),
    date: String(x.date ?? ''),
    note: String(x.note ?? ''),
    storeId: String(x.storeId ?? '')
  }
}

/** yyyy-mm-dd plus `days` business days (Mon–Fri). */
export function addBusinessDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number)
  if (!y || !m || !d) return ''
  const t = new Date(Date.UTC(y, m - 1, d))
  let left = days
  while (left > 0) {
    t.setUTCDate(t.getUTCDate() + 1)
    const day = t.getUTCDay()
    if (day !== 0 && day !== 6) left--
  }
  return t.toISOString().slice(0, 10)
}

/** Every release, newest first, with its sign-off and FAA notice. */
export async function listReleases(): Promise<Release[]> {
  const snap = await getDocs(query(collection(db, LOG), orderBy('createdAt', 'asc')))
  const entries = snap.docs.map((d) => fromDoc(d.id, d.data()))
  const followUps = entries.filter((e): e is FollowUpEntry => e.type !== 'release')
  // The latest of each kind counts (e.g. a corrected FAA date).
  const latest = (releaseId: string, type: FollowUpEntry['type']) =>
    followUps.filter((f) => f.releaseId === releaseId && f.type === type).pop() ?? null
  return entries
    .filter((e): e is ReleaseEntry => e.type === 'release')
    .map((r) => ({
      ...r,
      signoff: latest(r.id, 'signoff'),
      faaSent: latest(r.id, 'faaSent'),
      faaAccepted: latest(r.id, 'faaAccepted'),
      noticeDue: r.emergency ? addBusinessDays(r.deployedDate, 10) : ''
    }))
    .reverse()
}

/** The latest release logged for each module (by when it was logged). */
export const latestReleaseByModule = (releases: Release[]) =>
  Object.fromEntries(
    APP_MODULES.map((m) => [m.id, releases.find((r) => r.module === m.id) ?? null])
  ) as Record<string, Release | null>

function who() {
  const user = auth.currentUser
  if (!user) throw new Error('Sign in first.')
  return {
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    createdByName: user.displayName || user.email || ''
  }
}

export interface NewRelease {
  module: string
  version: string
  deployedDate: string
  description: string
  categories: RevisionCategory[]
  emergency: boolean
}

/** Logs a release (Development personnel — Dev). */
export async function addRelease(r: NewRelease): Promise<void> {
  const categories = moduleById(r.module)?.faaRegulated ? r.categories : []
  await addDoc(collection(db, LOG), {
    type: 'release',
    module: r.module,
    version: r.version.trim(),
    deployedDate: r.deployedDate,
    description: r.description.trim(),
    categories,
    regulatory: isRegulatory(r.module, categories),
    emergency: r.emergency && isRegulatory(r.module, categories),
    appVersion: APP_VERSION,
    commit: GIT_COMMIT,
    ...who()
  })
}

/**
 * A follow-up: the Chief Pilot's sign-off (password checked by the caller,
 * `storeId` = the school they're Chief Pilot of), or the FAA notice's sent /
 * accepted date.
 */
export async function addFollowUp(
  type: FollowUpEntry['type'],
  releaseId: string,
  fields: { date?: string; note?: string; storeId?: string }
): Promise<void> {
  await addDoc(collection(db, LOG), {
    type,
    releaseId,
    date: fields.date ?? '',
    note: (fields.note ?? '').trim(),
    storeId: fields.storeId ?? '',
    ...who()
  })
}

/** Where a regulatory release stands. */
export function releaseStatus(r: Release): { text: string; color: string } {
  if (!r.regulatory) return { text: 'Not regulatory-impacting', color: 'grey' }
  if (r.faaAccepted) return { text: `FAA accepted ${r.faaAccepted.date}`, color: 'success' }
  if (!r.signoff) return { text: 'Needs Chief Pilot sign-off', color: 'error' }
  if (r.faaSent)
    return { text: `FAA notice sent ${r.faaSent.date} — pending`, color: 'amber-darken-2' }
  return {
    text: r.emergency ? `FAA notice due by ${r.noticeDue}` : 'FAA notice not sent',
    color: 'error'
  }
}
